import { useRef, useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { GlobalTextarea } from './GlobalTextarea';
import { GlobalButton } from './GlobalButton';
import { useArchiveChat } from '../hooks/useArchiveChat';
import { useLocale } from '../context/DynamicLocaleProvider';
import { useToast } from '../context/ToastProvider';
import { useUser } from '../context/UserContext';
import { useTheme } from '../context/ThemeProvider';
import LocalLLMTrust from './LocalLLMTrust';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { collection, getDocs } from 'firebase/firestore';
import styles from './ArchiveChat.module.css';
import { db } from '../lib/firebase';

// CRITICAL: AI Knowledge Account ID for knowledge ingestion
// All knowledge documents must be associated with this account (ai@thescon.com)
const AI_KNOWLEDGE_USER_ID = 'ZKFh9kjq7Xgg7CbsawUqxqg7IwC2';

interface ArchiveChatProps {
  title?: string;
  paragraph?: string;
  selectedDocumentTitle?: string;
  additionalContext?: string;
  hideBorderTop?: boolean;
  enabled?: boolean;
  documentId?: string;
  projectId?: string;
  knowledgeId?: string;
}

export default function ArchiveChat({ 
  title, 
  paragraph, 
  selectedDocumentTitle, 
  additionalContext,
  hideBorderTop = false,
  enabled = false,
  documentId,
  projectId,
  knowledgeId
}: ArchiveChatProps = {}) {
  const {
    messages,
    question,
    setQuestion,
    sendMessage,
    isThinking,
    clearMessages,
  } = useArchiveChat(additionalContext, documentId, projectId, knowledgeId);

  const endRef = useRef<HTMLDivElement | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { t } = useLocale();
  const showToast = useToast();
  const { role } = useUser();
  const { theme } = useTheme();
  const isDarkMode = theme === 'dark';
  
  // Knowledge ingestion state
  const [isProcessingKnowledge, setIsProcessingKnowledge] = useState(false);
  const [processingStatus, setProcessingStatus] = useState('');
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  
  // Knowledge documents state
  const [knowledgeDocuments, setKnowledgeDocuments] = useState<{id: string, filename: string}[]>([]);
  const [loadingKnowledgeDocuments, setLoadingKnowledgeDocuments] = useState(false);
  
  // Typing animation state
  const [, setDisplayedParagraph] = useState('');
  const [, setIsTyping] = useState(false);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage();
  };

  const handleIngestKnowledge = () => {
    fileInputRef.current?.click();
  };

  // Fetch knowledge documents from Firestore
  const fetchKnowledgeDocuments = async () => {
    setLoadingKnowledgeDocuments(true);
    try {
      const knowledgeDocsRef = collection(db, 'knowledgeDocuments');
      const snapshot = await getDocs(knowledgeDocsRef);
      
      const documents = snapshot.docs.map(doc => ({
        id: doc.id,
        filename: doc.data().filename || t('ask_archive_sheet_unknown_file')
      }));
      
      setKnowledgeDocuments(documents);
    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Error fetching knowledge documents:', error);
      }
      showToast(t('ask_archive_sheet_failed_load_documents'), 'error');
    } finally {
      setLoadingKnowledgeDocuments(false);
    }
  };

  // Load knowledge documents on component mount
  useEffect(() => {
    fetchKnowledgeDocuments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Rotate through processing messages when processing is active
  useEffect(() => {
    if (!isProcessingKnowledge) {
      setCurrentMessageIndex(0);
      return;
    }

    const processingMessages = [
      t('ask_archive_sheet_upload_messages_uploading'),
      t('ask_archive_sheet_upload_messages_processing'),
      t('ask_archive_sheet_upload_messages_extracting'),
      t('ask_archive_sheet_upload_messages_embeddings'),
      t('ask_archive_sheet_upload_messages_saving'),
    ];

    // Only rotate if no specific status is set (like "Ready")
    if (processingStatus && !processingMessages.includes(processingStatus)) {
      return; // Don't rotate, show the specific status
    }

    // Rotate through messages automatically
    const interval = setInterval(() => {
      setCurrentMessageIndex((prev) => (prev + 1) % processingMessages.length);
    }, 2000); // Change message every 2 seconds

    return () => clearInterval(interval);
  }, [isProcessingKnowledge, processingStatus, t]);

  // Typing animation effect for paragraph
  useEffect(() => {
    if (!paragraph || messages.length > 0) {
      setDisplayedParagraph('');
      setIsTyping(false);
      return;
    }

    setIsTyping(true);
    setDisplayedParagraph('');
    
    const fullText = paragraph;
    let currentIndex = 0;
    
    const typingInterval = setInterval(() => {
      if (currentIndex < fullText.length) {
        setDisplayedParagraph(fullText.slice(0, currentIndex + 1));
        currentIndex++;
      } else {
        setIsTyping(false);
        clearInterval(typingInterval);
      }
    }, 30); // Adjust speed here (lower = faster)

    return () => {
      clearInterval(typingInterval);
    };
  }, [paragraph, messages.length]);

  const handleFileInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // CRITICAL: Always use the AI knowledge account for knowledge ingestion
    // This ensures all knowledge documents are associated with ai@thescon.com
    const knowledgeUserId = AI_KNOWLEDGE_USER_ID;

    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      showToast(t('ask_archive_sheet_file_type_error'), 'error');
      return;
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      showToast(t('ask_archive_sheet_file_size_error'), 'error');
      return;
    }

    try {
      // Reset states
      setIsProcessingKnowledge(true);
      setProcessingStatus(t('ask_archive_sheet_upload_messages_uploading'));

      // Upload file to Firebase Storage
      const storage = getStorage();
      const fileName = `knowledge/${knowledgeUserId}/${Date.now()}_${file.name}`;
      const storageRef = ref(storage, fileName);

      const uploadTask = uploadBytes(storageRef, file);
      await uploadTask;

      // Get download URL
      const downloadURL = await getDownloadURL(storageRef);
      setProcessingStatus(t('ask_archive_sheet_upload_messages_processing'));

      // Call the knowledge injection cloud function
      const response = await fetch('https://europe-west3-thescon.cloudfunctions.net/injectKnowledgeViaWeb', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          fileUrl: downloadURL,
          userId: knowledgeUserId, // Always use the AI knowledge account
          language: 'en', // You can make this dynamic based on user preferences
          filename: file.name,
          useCloudVision: true, // CRITICAL: Enable Google Cloud Vision OCR for full text extraction
          chunkSize: 800, // Standard chunk size for embeddings
          chunkOverlap: 200, // Standard overlap for context preservation
          enableEmbedding: true // Always enable embedding for knowledge base
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      setProcessingStatus(t('ask_archive_sheet_upload_messages_extracting'));

      const result = await response.json();

      // Check if the result contains the expected data
      if (result && result.success) {
        setProcessingStatus(t('ask_archive_sheet_upload_messages_ready'));
        
        // Wait a moment, then hide status and show success toast
        setTimeout(() => {
          setIsProcessingKnowledge(false);
          setProcessingStatus('');
          showToast(t('ask_archive_sheet_upload_success'), 'success');
          // Refresh the knowledge documents list
          fetchKnowledgeDocuments();
        }, 1000);
      } else {
        throw new Error('Invalid response from knowledge processing service');
      }

    } catch (error) {
      if (process.env.NODE_ENV !== 'production') {
        console.error('Knowledge upload error:', error);
      }
      setIsProcessingKnowledge(false);
      setProcessingStatus('');
      showToast(error instanceof Error ? error.message : t('ask_archive_sheet_upload_error'), 'error');
    }

    // Reset file input
    if (event.target) {
      event.target.value = '';
    }
  };


  return (
    <div className={`${styles.chatContainer} ${hideBorderTop ? styles.noBorderTop : ''}`}>

      {/* Chat header */}
      <div className={styles.chatHeader}>
        <div className={styles.headerTop}>
          <h2>{t('ask_archive_sheet_title')}</h2>
          {messages.length > 0 && (
            <button 
              className={styles.clearButton}
              onClick={clearMessages}
              title={t('ask_archive_sheet_clear_conversation')}
            >
              {t('ask_archive_sheet_clear_button')}
            </button>
          )}
        </div>
        {messages.length === 0 && (
          <>
            {false && role === 'internal_admin' && (
              <div className={styles.knowledgeFilesContainer}>
                <div className={styles.knowledgeFilesBubble}>
                  <select
                    className={styles.knowledgeFilesSelect}
                    onChange={(e) => {
                      if (e.target.value) {
                        // Handle knowledge file selection
                      }
                    }}
                    disabled={loadingKnowledgeDocuments}
                  >
                    <option value="">
                      {loadingKnowledgeDocuments 
                        ? t('ask_archive_sheet_loading')
                        : selectedDocumentTitle || t('ask_archive_sheet_view_knowledge_files')
                      }
                    </option>
                    {knowledgeDocuments.map((doc) => (
                      <option key={doc.id} value={doc.id}>
                        {doc.filename}
                      </option>
                    ))}
                  </select>
                  <img 
                    src={isDarkMode ? "/assets/icons/chevron.down.svg" : "/assets/icons/chevron.down.black.svg"} 
                    alt={t('ask_archive_sheet_dropdown_alt')} 
                    width={12} 
                    height={12} 
                    className={styles.chevronIcon}
                  />
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Chat messages - only render if there are messages or AI is thinking */}
      {(messages.length > 0 || isThinking) && (
        <div className={styles.chatMessages}>
          {/* Messages */}
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`${styles.messageWrapper} ${msg.isUser ? styles.userWrapper : styles.assistantWrapper}`}
            >
              {msg.isUser ? (
                <div className={`${styles.chatBubble} ${styles.user}`}>
                  {msg.text}
                </div>
              ) : (
                <div className={`${styles.chatBubble} ${styles.assistant}`}>
                  <div className={styles.markdownContent}>
                    <ReactMarkdown
                      remarkPlugins={[remarkGfm]}
                      components={{
                        p: ({ children }) => <p className={styles.markdownParagraph}>{children}</p>,
                        ul: ({ children }) => <ul className={styles.markdownList}>{children}</ul>,
                        ol: ({ children }) => <ol className={styles.markdownList}>{children}</ol>,
                        li: ({ children }) => <li className={styles.markdownListItem}>{children}</li>,
                        code: ({ inline, className, children, ...props }: any) => {
                          return inline ? (
                            <code className={styles.markdownInlineCode} {...props}>{children}</code>
                          ) : (
                            <code className={styles.markdownCodeBlock} {...props}>{children}</code>
                          );
                        },
                        pre: ({ children }) => <pre className={styles.markdownPre}>{children}</pre>,
                        h1: ({ children }) => <h1 className={styles.markdownH1}>{children}</h1>,
                        h2: ({ children }) => <h2 className={styles.markdownH2}>{children}</h2>,
                        h3: ({ children }) => <h3 className={styles.markdownH3}>{children}</h3>,
                        blockquote: ({ children }) => <blockquote className={styles.markdownBlockquote}>{children}</blockquote>,
                        strong: ({ children }) => <strong className={styles.markdownStrong}>{children}</strong>,
                        em: ({ children }) => <em className={styles.markdownEm}>{children}</em>,
                        a: ({ href, children }) => <a href={href} className={styles.markdownLink} target="_blank" rel="noopener noreferrer">{children}</a>,
                      }}
                    >
                      {msg.text}
                    </ReactMarkdown>
                  </div>
                </div>
              )}
            </div>
          ))}

          {/* Thinking */}
          {isThinking && (
            <div className={styles.typing}>
              <div className={styles.thinkingDots}>
                <span></span>
                <span></span>
                <span></span>
              </div>
              {t('ask_archive_sheet_thinking_text')}
            </div>
          )}

          <div ref={endRef} />
        </div>
      )}

      {/* Chat input bar */}
      <form className={styles.chatInputBar} onSubmit={handleSubmit}>

        {/* Chat input */}
        <GlobalTextarea
          label={t('ask_archive_sheet_input_placeholder')}
          placeholder={t('ask_archive_sheet_input_placeholder')}
          value={question}
          onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setQuestion(e.target.value)}
          disabled={!enabled}
        />

        {/* Buttons */}
        <div className={styles.buttonWrapper}>

          {/* Ask question button */}
        <GlobalButton
          type="submit"
          className={styles.sendButton}
          disabled={!enabled}
        >
          {t('ask_archive_sheet_send_message')}
        </GlobalButton>

{/* Ingest knowledge button - only visible to internal_admin */}
        {false && role === 'internal_admin' && (
          <GlobalButton
            type="button"
            className={styles.ingestButton}
            variant="secondary"
            onClick={handleIngestKnowledge}
            disabled={!enabled}
            icon={<img src={ isDarkMode ? "/assets/icons/icloud.and.arrow.up.svg" : "/assets/icons/icloud.and.arrow.up.black.svg"} alt={t('ask_archive_sheet_ingest_knowledge_alt')} width={12} height={12} />}
          >
            {t('ask_archive_sheet_ingest_knowledge_button')}
          </GlobalButton>
        )}

        </div>

        {/* Small processing status indicator */}
        {isProcessingKnowledge && (
          <div className={styles.processingStatus}>
            <video
              autoPlay
              muted
              loop
              playsInline
              className={styles.processingVideo}
              src="/assets/videos/processing-animation-03.mp4"
            />
            <span className={styles.processingText}>
              {processingStatus || (() => {
                const rotatingMessages = [
                  t('ask_archive_sheet_upload_messages_uploading'),
                  t('ask_archive_sheet_upload_messages_processing'),
                  t('ask_archive_sheet_upload_messages_extracting'),
                  t('ask_archive_sheet_upload_messages_embeddings'),
                  t('ask_archive_sheet_upload_messages_saving'),
                ];
                return rotatingMessages[currentMessageIndex];
              })()}
            </span>
          </div>
        )}
        
      </form>

      {/* Hidden file input for knowledge ingestion */}
      <input
        type="file"
        ref={fileInputRef}
        style={{ display: 'none' }}
        onChange={handleFileInputChange}
        accept=".pdf,.jpg,.jpeg,.png"
      />

      <LocalLLMTrust />
    </div>
  );
}